import React, { ReactNode, KeyboardEvent, MouseEvent } from 'react';

import classNames from 'classnames';
import { Fade } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import { Box } from '@vk-hr-tek/ui/Box/Box';
import { Link } from '@vk-hr-tek/ui/Link';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Typography, TypographyColor } from '@vk-hr-tek/ui/Typography';

import { useUrlWithPrefix } from '../../../hooks';
import { TIMEOUT_FOR_FADE } from '../Sidebar.constants';

import { useStyles } from './SidebarItem.styles';

interface SidebarItemProps {
  title: string;
  color?: TypographyColor;
  sidebarOpened: boolean;
  icon: ReactNode;
  href: string;
  isActive: boolean;
  aqaClass: string;
  skeletonLoading: boolean;
  closeDrawer?: (event: KeyboardEvent | MouseEvent) => void;
  onClick: (pageTitle: string) => void;
  external?: boolean;
  blank?: boolean;
  isCustom?: boolean;
}

export const SidebarItem = ({
  title,
  color,
  sidebarOpened,
  skeletonLoading,
  href,
  aqaClass,
  icon,
  isActive,
  closeDrawer,
  onClick,
  external = false,
  blank = false,
  isCustom = false,
}: SidebarItemProps) => {
  const { classes } = useStyles();
  const urlWithPrefix = useUrlWithPrefix(external);

  const renderAqaClass = (className: string): string =>
    `${aqaClass}_${className}`;

  const onCustomAction = () => {
    if (!isActive) {
      onClick(title);
    }
  };

  if (isCustom) {
    return (
      <Box>
        <a
          href={href}
          onClick={closeDrawer}
          className={classNames(
            classes.menuItem,
            isActive && classes.active,
            aqaClass,
          )}
          target={blank ? '_blank' : '_self'}
          style={{ textDecoration: 'none', color: 'inherit' }}
          rel="noopener noreferrer"
        >
          <Tooltip title={title} placement="right" disabled={sidebarOpened}>
            <Box
              p="8"
              display="flex"
              minWidth={40}
              minHeight={40}
              alignItems="center"
              justifyContent="center"
              className={renderAqaClass('menuItemWithoutSubmenuIcon')}
            >
              {skeletonLoading ? <Skeleton width="100%" /> : icon}
            </Box>
          </Tooltip>

          <Fade in={sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
            <Box minWidth={0}>
              <Typography
                variant="body3"
                noWrap
                color={color ? color : 'text.light.primary'}
                className={classNames(
                  renderAqaClass('menuItemWithoutSubmenuText'),
                  classes.truncatedText,
                )}
              >
                {skeletonLoading ? <Skeleton width={200} /> : title}
              </Typography>
            </Box>
          </Fade>
        </a>
      </Box>
    );
  }

  return (
    <Box>
      <Link
        {...urlWithPrefix(href)}
        onClick={closeDrawer}
        onCustomAction={onCustomAction}
        variant="simple"
      >
        <Box
          className={classNames(
            classes.menuItem,
            isActive && classes.active,
            aqaClass,
          )}
        >
          <Tooltip title={title} placement="right" disabled={sidebarOpened}>
            <Box
              p="8"
              display="flex"
              minWidth={40}
              minHeight={40}
              alignItems="center"
              justifyContent="center"
              className={renderAqaClass('menuItemWithoutSubmenuIcon')}
            >
              {skeletonLoading ? <Skeleton width="100%" /> : icon}
            </Box>
          </Tooltip>

          <Fade in={sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
            <Box minWidth={0}>
              <Typography
                variant="body3"
                noWrap
                color={color ? color : 'text.light.primary'}
                className={classNames(
                  renderAqaClass('menuItemWithoutSubmenuText'),
                  classes.truncatedText,
                )}
              >
                {skeletonLoading ? <Skeleton width={200} /> : title}
              </Typography>
            </Box>
          </Fade>
        </Box>
      </Link>
    </Box>
  );
};
